import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import {
  Typography,
  TextField,
  FormControl,
  Button,
  IconButton,
  Select,
  MenuItem,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { CREATE_BANK_ACCOUNT, EDIT_BANK_ACCOUNT, GET_BANK_ACCOUNT_TYPES, GET_BANKS } from '../../graphql';
import { validEmail } from '../../helpers';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';

const useStyles = makeStyles({
  label: {
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    marginBottom: '1rem',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  error: {
    marginBottom: '0.5rem',
    marginLeft: '0.5rem',
  },
});

const BankAccountForm = ({ mode, cancel, onSave, accounts, account, company }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { defaultMatches: true });
  const country = useGetCountryFromUrl();

  const { data: accountTypes, loading: loadingAccountTypes } = useQuery(GET_BANK_ACCOUNT_TYPES, {
    variables: {
      countryId: country?.id,
    } });
  const { data: banks } = useQuery(GET_BANKS, {
    variables: {
      countryId: country?.id,
    } });
  const [createNewAccount, { loading: newLoading }] = useMutation(
    CREATE_BANK_ACCOUNT,
    {
      variables: { accountRut: `${company.masterEntity.displayNationalIdentifier.replace(/\./g, '')}` },
      onCompleted: (data) => { onSave(data.createBankAccount.bankAccountCreated); },
      onError: () => {},
    },
  );

  const [editBankAccount, { loading: editLoading }] = useMutation(
    EDIT_BANK_ACCOUNT,
    {
      variables: { id: account?.id },
      onCompleted: (data) => { onSave(data.editBankAccount.bankAccount); },
      onError: () => { cancel(); },
    },
  );

  const loading = newLoading || editLoading;

  const mutation = (data) => (mode === 'new' ? createNewAccount(data) : editBankAccount(data));

  const [data, setData] = useState({
    number: { value: parseInt(account?.accountNumber, 10), error: '' },
    name: { value: account?.accountName, error: '' },
    email: { value: account?.accountEmail, error: '' },
    bank: { value: account?.bankName, error: '' },
    accountType: { value: account?.accountType, error: '' },
  });

  const [formError, setFormError] = useState('');
  const [accountTypeError, setAccountTypeError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setData((oldData) => ({ ...oldData, [name]: { ...oldData[name], value } }));
    if (name === 'bank') setFormError('');
    else if (name === 'accountType') setAccountTypeError('');
  };

  const cleanError = (field) => {
    setData((oldData) => ({ ...oldData, [field]: { ...oldData[field], error: '' } }));
  };

  const handleSave = async () => {
    const { number, name, email, bank, accountType } = data;
    if (name.value === '') {
      setData((oldData) => ({ ...oldData, name: { ...oldData.name, error: 'El nombre de la cuenta no puede estar vacío' } }));
      return;
    }
    cleanError('name');
    if (number.value === '') {
      setData((oldData) => ({ ...oldData, number: { ...oldData.number, error: 'El número de la cuenta no puede estar vacío' } }));
      return;
    }
    cleanError('number');
    if (bank.value === '') {
      setFormError('Debes elegir algún banco');
      return;
    }
    setFormError('');
    if (accountType.value === '') {
      setAccountTypeError('Debes elegir algún tipo de cuenta');
      return;
    }
    setAccountTypeError('');
    if (!validEmail(email.value)) {
      setData((oldData) => ({ ...oldData, email: { ...oldData.email, error: 'Ingresa un email válido' } }));
      return;
    }
    cleanError('email');
    for (let i = 0; i < accounts?.length; i += 1) {
      const accountExists = accounts[i];
      if (accountExists.bankName === bank.value && accountExists.accountNumber === number.value) {
        setData((oldData) => ({ ...oldData, number: { ...oldData.number, error: 'Ya registraste esta cuenta' } }));
        return;
      }
    }
    cleanError('number');
    setFormError('');
    mutation({
      variables: {
        bankName: bank.value,
        accountEmail: email.value,
        accountName: name.value,
        accountNumber: number.value,
        accountType: accountType.value,
      },
    });
  };

  return (
    <>
      <Grid container alignItems="center" sx={{ marginBottom: '2rem' }}>
        <Grid item xs={2}>
          <IconButton onClick={cancel} size="large">
            <ArrowBackIos />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <Typography color="primary" variant="h6">
            {mode === 'edit' ? 'Edita tu' : 'Registra una'} cuenta bancaria
          </Typography>
        </Grid>
      </Grid>
      <Typography className={classes.label}><b>Titular de la cuenta</b></Typography>
      <TextField
        className={classes.input}
        variant="outlined"
        name="name"
        value={data.name.value}
        error={data.name.error !== ''}
        helperText={data.name.error}
        onChange={handleChange}
      />
      <Typography className={classes.label}><b>Número de la cuenta</b></Typography>
      <TextField
        className={classes.input}
        variant="outlined"
        name="number"
        type="number"
        value={data.number.value}
        error={data.number.error !== ''}
        helperText={data.number.error}
        onChange={handleChange}
      />
      <Typography className={classes.label}><b>Banco</b></Typography>
      <FormControl variant="outlined" fullWidth>
        <Select
          className={classes.input}
          name="bank"
          value={data.bank.value}
          error={formError !== ''}
          onChange={handleChange}
        >
          <MenuItem value="" disabled>Elige un banco</MenuItem>
          {banks?.getBankOptionsByCountry.map((bank) => (
            <MenuItem key={bank.name} value={bank.name}>{bank.name}</MenuItem>
          ))}
        </Select>
        {formError && <Typography color="error" variant="caption" className={classes.error}>{formError}</Typography>}
      </FormControl>
      <Typography className={classes.label}><b>Tipo de cuenta</b></Typography>
      <FormControl variant="outlined" fullWidth>
        <Select
          className={classes.input}
          name="accountType"
          value={data.accountType.value}
          error={accountTypeError !== ''}
          onChange={handleChange}
        >
          <MenuItem value="" disabled>Elige un tipo de cuenta</MenuItem>
          {!loadingAccountTypes && accountTypes?.getBankAccountTypesByCountry.map((type) => (
            <MenuItem key={type.name} value={type.name}>{type.name}</MenuItem>
          ))}
        </Select>
        {accountTypeError && <Typography color="error" variant="caption" className={classes.error}>{accountTypeError}</Typography>}
      </FormControl>
      <Typography className={classes.label}><b>Email</b></Typography>
      <TextField
        className={classes.input}
        variant="outlined"
        name="email"
        value={data.email.value}
        error={data.email.error !== ''}
        helperText={data.email.error}
        onChange={handleChange}
      />
      <div className={classes.buttonsContainer}>
        <Button
          variant="contained"
          color="primary"
          sx={{
            width: '50%',
            maxWidth: '250px',
            height: '100%',
            alignSelf: isMobile ? 'center' : 'flex-end',
          }}
          onClick={handleSave}
          endIcon={loading ? <CircularProgress size={16} /> : null}
          disabled={loading}
        >
          {mode === 'edit' ? 'Editar' : 'Registrar'} cuenta
        </Button>
      </div>
    </>
  );
};

BankAccountForm.propTypes = {
  mode: PropTypes.string.isRequired,
  cancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape({
    bankName: PropTypes.string.isRequired,
    accountNumber: PropTypes.string.isRequired,
  })).isRequired,
  account: PropTypes.shape({
    id: PropTypes.number.isRequired,
    bankName: PropTypes.string.isRequired,
    accountNumber: PropTypes.string.isRequired,
    accountType: PropTypes.string.isRequired,
    accountEmail: PropTypes.string.isRequired,
    accountName: PropTypes.string.isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  company: PropTypes.object.isRequired,
};

export default BankAccountForm;
